import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Button, Badge, Anchor } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  StatusBadge,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { TenantName } from '../../../components/TenantName';
import { CopyableId } from '../../../components/CopyableId';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { RowMenu } from '../../../components/RowMenu';
import { useTenantMap } from '../../../data/useTenants';
import { QUALITY_RATINGS, countByRating, normaliseRating, qualityTone } from '../lib/quality';
import {
  WHATSAPP_MANAGER_URL,
  hasOpenNameRequest,
  nameStatusLabel,
  nameStatusTone,
  payerKey,
  payerLabel,
  readableCode
} from '../lib/displayName';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
type PhoneNumber = admin.AdminPhoneNumber;
type KpiKey = 'all' | 'GREEN' | 'YELLOW' | 'LOW' | 'RED' | 'other' | 'unrated' | 'disabled';

// The service orders by CreatedAt DESC and stops here. Saying so keeps the
// totals honest: past this point the page describes what it was sent, not the
// platform.
const SERVER_ROW_CAP = 500;

function openWhatsAppManager() {
  window.open(WHATSAPP_MANAGER_URL, '_blank', 'noopener,noreferrer');
}

export default function NumbersPage() {
  const t = useT();
  const nav = useNavigate();
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'numbers'],
    queryFn: () => admin.listPhoneNumbers()
  });
  const all = useMemo(() => (data?.data ?? []) as PhoneNumber[], [data]);
  const capped = all.length >= SERVER_ROW_CAP;
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.numbers.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.numbers.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  // Name requests are work for the owner, not a quality band, so they get a
  // toggle of their own beside the drawer rather than a chip in the row above.
  const [nameRequestsOnly, setNameRequestsOnly] = useState(false);

  const ratings = useMemo(() => countByRating(all), [all]);
  const disabled = useMemo(() => all.filter((r) => !r.is_active).length, [all]);
  const openNameRequests = useMemo(() => all.filter(hasOpenNameRequest).length, [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('gateways.numbers.kpi.all'), value: all.length, tone: 'primary' },
    ...QUALITY_RATINGS.map((rating) => ({
      id: rating,
      label: rating,
      value: ratings[rating],
      tone: qualityTone(rating)
    })),
    // Only offered when Meta has sent a rating this build does not recognise,
    // so such a number is reachable instead of matching nothing but "All".
    ...(ratings.other > 0 ? [{ id: 'other', label: t('gateways.numbers.kpi.other'), value: ratings.other, tone: 'inactive' as const }] : []),
    { id: 'unrated', label: t('gateways.numbers.kpi.unrated'), value: ratings.unrated, tone: 'inactive' },
    { id: 'disabled', label: t('status.disabled'), value: disabled, tone: 'pending' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // Quality and status live in the chips above; repeating them here let an
    // operator set RED in one place and GREEN in the other and get nothing.
    const orgs = Array.from(new Set(all.map((r) => r.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const wabas = Array.from(new Set(all.map((r) => r.meta_waba_id).filter((w): w is string => Boolean(w)))).sort();
    return [
      {
        key: 'organisation',
        label: t('gateways.numbers.filters.tenant'),
        placeholder: t('gateways.numbers.filters.anyTenant'),
        options: orgs
      },
      {
        key: 'waba',
        label: t('gateways.numbers.columns.waba'),
        placeholder: t('gateways.numbers.filters.anyWaba'),
        options: wabas.map((id) => ({ value: id, label: id }))
      },
      {
        key: 'payer',
        label: t('gateways.numbers.columns.metaBilledTo'),
        placeholder: t('gateways.numbers.filters.anyPayer'),
        options: (['platform', 'customer', 'unknown'] as const).map((value) => ({ value, label: t(`gateways.numbers.payer.${value}`) }))
      }
    ];
  }, [all, t, tenants]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0 || nameRequestsOnly;
  function clearAllFilters() { setQuery(''); setKpi('all'); setFilters({}); setNameRequestsOnly(false); setPage(1); }

  // Each narrowing spelled out, so a short list is never mistaken for a small
  // inventory. The chip and the drawer both feed the same row.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('gateways.numbers.filters.quality'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('gateways.numbers.filters.tenant'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.waba) pills.push({ id: 'waba', field: t('gateways.numbers.columns.waba'), value: filters.waba });
  if (filters.payer) pills.push({ id: 'payer', field: t('gateways.numbers.columns.metaBilledTo'), value: t(`gateways.numbers.payer.${payerKey(filters.payer)}`) });
  if (nameRequestsOnly) pills.push({ id: 'nameRequests', field: t('gateways.numbers.nameRequests.filter'), value: t('gateways.numbers.nameRequests.openOnly') });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    if (id === 'nameRequests') return setNameRequestsOnly(false);
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notifySuccess(value, t('common.copied'));
    } catch {
      // A denied clipboard is the browser's decision to report, not ours.
    }
  }

  /** "who, when" for a name request, leaving out whichever half is missing. */
  function requestedByWhen(r: PhoneNumber): string {
    const when = r.name_requested_at ? formatRelative(r.name_requested_at) : '';
    const who = r.name_requested_by?.trim() ?? '';
    if (who && when) return t('gateways.numbers.nameRequest.byWhen', { who, when });
    if (when) return t('gateways.numbers.nameRequest.when', { when });
    return who;
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi && kpi !== 'all') {
      if (kpi === 'unrated') rows = rows.filter((r) => normaliseRating(r.quality_rating) === null);
      else if (kpi === 'other') rows = rows.filter((r) => normaliseRating(r.quality_rating) === 'OTHER');
      else if (kpi === 'disabled') rows = rows.filter((r) => !r.is_active);
      else rows = rows.filter((r) => normaliseRating(r.quality_rating) === kpi);
    }
    if (filters.organisation) rows = rows.filter((r) => r.organisation_id === filters.organisation);
    if (filters.waba) rows = rows.filter((r) => r.meta_waba_id === filters.waba);
    if (filters.payer) rows = rows.filter((r) => payerKey(r.meta_billing_payer) === filters.payer);
    if (nameRequestsOnly) rows = rows.filter(hasOpenNameRequest);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) =>
      (r.msisdn ?? '').toLowerCase().includes(q) ||
      (r.display_name ?? '').toLowerCase().includes(q) ||
      (r.name_requested ?? '').toLowerCase().includes(q) ||
      (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q)
    );
    return rows;
  }, [all, kpi, filters, query, tenants, nameRequestsOnly]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={t('gateways.numbers.title')}
        description={t('gateways.numbers.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.refresh')}</Button>
            <Text role="caption" c={MUTED}>{t('common.lastUpdated', { time: formatDateTime(new Date(dataUpdatedAt).toISOString()) })}</Text>
          </Stack>
        }
      />

      {/* The quality split is the reason this page exists, so the chips lead
          on the page ground and the pills say what is currently applied. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      {/* A business on the shared account cannot rename its own number: the
          owner makes the change in WhatsApp Manager. Said here so a request
          is not left waiting in a column nobody scrolls to. */}
      {!error && openNameRequests > 0 && (
        <Alert color="warning" variant="light" title={t('gateways.numbers.nameRequests.bannerTitle', { count: formatInteger(openNameRequests) })}>
          <Stack gap="sm" align="flex-start">
            <Text role="bodySmall">{t('gateways.numbers.nameRequests.bannerBody')}</Text>
            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => { setNameRequestsOnly((v) => !v); setPage(1); }}>
                {nameRequestsOnly ? t('gateways.numbers.nameRequests.showAll') : t('gateways.numbers.nameRequests.show')}
              </Button>
              <Button size="xs" onClick={openWhatsAppManager}>{t('gateways.numbers.openWhatsAppManager')}</Button>
            </Group>
          </Stack>
        </Alert>
      )}

      <TableShell
        title={t('gateways.numbers.tableTitle')}
        totalLabel={
          capped
            ? t('gateways.numbers.totalCapped', { shown: filtered.length, total: all.length })
            : t('gateways.numbers.totalLabel', { shown: filtered.length, total: all.length })
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('gateways.numbers.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <Button
              size="xs"
              variant={nameRequestsOnly ? 'filled' : 'outline'}
              aria-pressed={nameRequestsOnly}
              onClick={() => { setNameRequestsOnly((v) => !v); setPage(1); }}
              rightSection={<Badge size="xs" variant={openNameRequests > 0 ? 'filled' : 'light'} color={openNameRequests > 0 ? 'orange' : 'gray'}>{formatInteger(openNameRequests)}</Badge>}
            >
              {t('gateways.numbers.nameRequests.filter')}
            </Button>
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('gateways.numbers.empty')} description={hasActiveFilters ? t('common.tryClearingFilters') : t('gateways.numbers.emptyRows')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={error ? (
          <Alert color="error" title={t('gateways.numbers.loadError')}>
            <Stack gap="sm" align="flex-start">
              <Text role="bodySmall">{(error as Error).message}</Text>
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Stack>
          </Alert>
        ) : null}
        pagination={
          <Stack gap="sm" style={{ width: '100%' }}>
            {capped && !error && (
              <Alert color="info" variant="light">{t('gateways.numbers.capNotice', { cap: SERVER_ROW_CAP })}</Alert>
            )}
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={capped}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </Stack>
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<PhoneNumber>>
            records={paged as Indexed<PhoneNumber>[]}
            loading={isLoading}
            idAccessor="id"
            emptyState={t('gateways.numbers.emptyRows')}
            onRowClick={(r) => nav(`/tenants/${r.organisation_id}`)}
            columns={[
              {
                // The number leads, with the business account's display name
                // under it: the number is what a ticket quotes, the name is
                // what tells two of a tenant's numbers apart.
                accessor: 'msisdn',
                title: t('gateways.numbers.columns.number'),
                sortable: true,
                render: (r) => (
                  <TwoLineCell
                    primary={r.msisdn ?? t('gateways.numbers.unnamed')}
                    secondary={r.display_name ?? undefined}
                    title={r.msisdn ?? undefined}
                  />
                )
              },
              { accessor: 'organisation_id', title: t('gateways.numbers.columns.tenant'), render: (r) => <TenantName id={r.organisation_id} /> },
              {
                accessor: 'meta_billing_payer',
                title: (
                  <Tooltip label={t('gateways.numbers.metaBilledToHint')} position="top" maxWidth={280}>
                    <span>{t('gateways.numbers.columns.metaBilledTo')}</span>
                  </Tooltip>
                ),
                render: (r) => payerKey(r.meta_billing_payer) === 'unknown'
                  ? <Text role="caption" c={MUTED}>{payerLabel(t, r.meta_billing_payer)}</Text>
                  : <Text role="bodySmall">{payerLabel(t, r.meta_billing_payer)}</Text>
              },
              {
                // Meta's review of the display name, with its reason when it
                // declined, so the owner knows what to change before asking again.
                accessor: 'name_status',
                title: t('gateways.numbers.columns.nameStatus'),
                render: (r) => (
                  <Stack gap={2}>
                    <StatusBadge status={nameStatusTone(r.name_status)} size="xs">{nameStatusLabel(t, r.name_status)}</StatusBadge>
                    {r.name_rejection_reason && (
                      <Text role="caption" c={MUTED}>{t('gateways.numbers.nameRejection', { reason: readableCode(r.name_rejection_reason) })}</Text>
                    )}
                  </Stack>
                )
              },
              {
                accessor: 'name_requested',
                title: t('gateways.numbers.columns.nameRequest'),
                render: (r) => hasOpenNameRequest(r)
                  ? (
                    <Stack gap={2}>
                      <TwoLineCell
                        primary={r.name_requested}
                        secondary={<span title={r.name_requested_at ? formatDateTime(r.name_requested_at) : undefined}>{requestedByWhen(r)}</span>}
                        title={r.name_requested ?? undefined}
                      />
                      <Anchor href={WHATSAPP_MANAGER_URL} target="_blank" rel="noopener noreferrer" size="xs" onClick={(e) => e.stopPropagation()}>
                        {t('gateways.numbers.openWhatsAppManager')}
                      </Anchor>
                    </Stack>
                  )
                  : <Text role="caption" c={MUTED}>{t('common.none')}</Text>
              },
              {
                // Operators paste this into Meta's console, so it stays on the
                // page and gains a copy button rather than being the smallest
                // text in the table.
                accessor: 'meta_phone_number_id',
                title: t('gateways.numbers.columns.metaId'),
                render: (r) => <CopyableId value={r.meta_phone_number_id} label={t('gateways.numbers.copyMetaId')} chars={12} />
              },
              {
                accessor: 'meta_waba_id',
                title: t('gateways.numbers.columns.waba'),
                render: (r) => r.meta_waba_id
                  ? <CopyableId value={r.meta_waba_id} label={t('gateways.numbers.copyWaba')} chars={12} />
                  : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
              },
              {
                accessor: 'quality_rating',
                title: (
                  <Tooltip label={t('gateways.numbers.qualityStale')} position="top">
                    <span>{t('gateways.numbers.columns.quality')}</span>
                  </Tooltip>
                ),
                render: (r) => r.quality_rating
                  ? <StatusBadge status={qualityTone(r.quality_rating)} size="xs">{r.quality_rating}</StatusBadge>
                  : <Text role="caption" c={MUTED}>{t('gateways.numbers.kpi.unrated')}</Text>
              },
              {
                accessor: 'is_active',
                title: t('common.status'),
                render: (r) => <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>{r.is_active ? t('status.active') : t('status.disabled')}</StatusBadge>
              },
              {
                accessor: 'registered_at',
                title: t('gateways.numbers.columns.registered'),
                sortable: true,
                render: (r) => r.registered_at
                  ? <span title={formatDateTime(r.registered_at)}>{formatRelative(r.registered_at)}</span>
                  : <Text role="caption" c={MUTED}>{t('gateways.numbers.neverRegistered')}</Text>
              },
              {
                // Without this an attached-but-never-registered number has no
                // age, so nobody can tell a fresh attachment from a stuck one.
                accessor: 'created_at',
                title: t('gateways.numbers.columns.attached'),
                sortable: true,
                render: (r) => <span title={formatDateTime(r.created_at)}>{formatRelative(r.created_at)}</span>
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                width: 64,
                textAlign: 'right',
                render: (r) => (
                  <RowMenu
                    actions={[
                      { id: 'tenant', label: t('gateways.numbers.actions.openTenant'), onSelect: () => nav(`/tenants/${r.organisation_id}`) },
                      ...(r.msisdn ? [{ id: 'msisdn', label: t('gateways.numbers.actions.copyMsisdn'), onSelect: () => void copy(r.msisdn as string) }] : []),
                      { id: 'metaId', label: t('gateways.numbers.copyMetaId'), onSelect: () => void copy(r.meta_phone_number_id) },
                      ...(r.meta_waba_id ? [{ id: 'waba', label: t('gateways.numbers.copyWaba'), onSelect: () => void copy(r.meta_waba_id as string) }] : []),
                      ...(hasOpenNameRequest(r) ? [{ id: 'manager', label: t('gateways.numbers.openWhatsAppManager'), onSelect: openWhatsAppManager }] : [])
                    ]}
                  />
                )
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="xs">
            {paged.map((r) => (
              <button key={r.id} type="button" onClick={() => nav(`/tenants/${r.organisation_id}`)} className="rr-list-row">
                <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <Text role="subtitle" fw={600} truncate>{r.display_name ?? r.msisdn ?? t('gateways.numbers.unnamed')}</Text>
                      {r.msisdn && <Text role="caption" c={MUTED}><code>{r.msisdn}</code></Text>}
                    </Group>
                    <Group gap="xs" align="baseline" wrap="nowrap" style={{ minWidth: 0 }}>
                      <TenantName id={r.organisation_id} subtle />
                      <Text role="caption" c={MUTED}>
                        {r.registered_at
                          ? `· ${t('gateways.numbers.list.registered', { when: formatRelative(r.registered_at) })}`
                          : `· ${t('gateways.numbers.neverRegistered')}`}
                      </Text>
                      <Text role="caption" c={MUTED}>{`· ${t('gateways.numbers.columns.metaBilledTo')}: ${payerLabel(t, r.meta_billing_payer)}`}</Text>
                    </Group>
                    {hasOpenNameRequest(r) && (
                      <Text role="caption" c="var(--rr-color-warning)" truncate>
                        {`${t('gateways.numbers.nameRequest.list', { name: r.name_requested as string })} · ${requestedByWhen(r)}`}
                      </Text>
                    )}
                  </Stack>
                  <StatusBadge status={nameStatusTone(r.name_status)} size="xs">{nameStatusLabel(t, r.name_status)}</StatusBadge>
                  {r.quality_rating && (
                    <StatusBadge status={qualityTone(r.quality_rating)} size="xs">{r.quality_rating}</StatusBadge>
                  )}
                  <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>{r.is_active ? t('status.active') : t('status.disabled')}</StatusBadge>
                </Group>
              </button>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<PhoneNumber>
            records={paged}
            idAccessor="id"
            cols={3}
            spacing="md"
            onCardClick={(r) => nav(`/tenants/${r.organisation_id}`)}
            renderCard={(r) => (
              <Stack gap="sm">
                <Group justify="space-between" align="baseline">
                  <Stack gap={2}>
                    <Text role="subtitle" fw={600} truncate>{r.display_name ?? r.msisdn ?? t('gateways.numbers.unnamed')}</Text>
                    {r.msisdn && <Text role="caption" c={MUTED}><code>{r.msisdn}</code></Text>}
                  </Stack>
                  {r.quality_rating && <StatusBadge status={qualityTone(r.quality_rating)} size="xs">{r.quality_rating}</StatusBadge>}
                </Group>
                <TenantName id={r.organisation_id} subtle />
                <Group gap="xs" align="center" wrap="wrap">
                  <Text role="caption" c={MUTED}>{`${t('gateways.numbers.columns.metaBilledTo')}: ${payerLabel(t, r.meta_billing_payer)}`}</Text>
                  <StatusBadge status={nameStatusTone(r.name_status)} size="xs">{nameStatusLabel(t, r.name_status)}</StatusBadge>
                </Group>
                {r.name_rejection_reason && (
                  <Text role="caption" c={MUTED}>{t('gateways.numbers.nameRejection', { reason: readableCode(r.name_rejection_reason) })}</Text>
                )}
                {hasOpenNameRequest(r) && (
                  <Stack gap={2}>
                    <Text role="caption" c="var(--rr-color-warning)">{t('gateways.numbers.nameRequest.list', { name: r.name_requested as string })}</Text>
                    <Text role="caption" c={MUTED}>{requestedByWhen(r)}</Text>
                  </Stack>
                )}
                <Group gap="xs" align="center" wrap="nowrap">
                  <Text role="caption" c={MUTED}>{t('gateways.numbers.columns.metaId')}</Text>
                  <CopyableId value={r.meta_phone_number_id} label={t('gateways.numbers.copyMetaId')} chars={10} />
                </Group>
                <Group justify="space-between" align="baseline" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
                  <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>{r.is_active ? t('status.active') : t('status.disabled')}</StatusBadge>
                  <Text role="caption" c={MUTED}>
                    {r.registered_at ? formatRelative(r.registered_at) : t('gateways.numbers.neverRegistered')}
                  </Text>
                </Group>
              </Stack>
            )}
          />
        )}
      </TableShell>
    </Stack>
  );
}
